"use client";

import posthog, { type CaptureResult } from "posthog-js";
import { type ReactNode } from "react";
import {
  isInternalCaptureBlocked,
  persistInternalCaptureBlock,
  shouldDropInternalCaptureEvent,
} from "@/lib/posthog-internal-user";

let posthogInitialized = false;

export function isPostHogInitialized() {
  return posthogInitialized;
}

function dropInternalOperatorEvents(event: CaptureResult): CaptureResult | null {
  if (isInternalCaptureBlocked()) return null;
  if (shouldDropInternalCaptureEvent(event)) {
    persistInternalCaptureBlock();
    return null;
  }
  return event;
}

function initializePostHog() {
  const apiKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST;

  if (!apiKey || !apiHost) {
    if (process.env.NODE_ENV !== "production") {
      const missingVariable = !apiKey
        ? "NEXT_PUBLIC_POSTHOG_KEY"
        : "NEXT_PUBLIC_POSTHOG_HOST";

      throw new Error(
        `${missingVariable} variable required by PostHog is missing or un-configured, this causes events to be silently missed. This error stops appearing once ${missingVariable} is configured`,
      );
    }

    return;
  }

  const skipInternalCapture = isInternalCaptureBlocked();

  posthog.init(apiKey, {
    api_host: apiHost,
    defaults: "2026-05-30",
    capture_exceptions: true,
    debug: process.env.NODE_ENV === "development",
    opt_out_capturing_by_default: skipInternalCapture,
    disable_session_recording: skipInternalCapture,
    before_send: dropInternalOperatorEvents,
  });
  if (skipInternalCapture) {
    posthog.opt_out_capturing();
  }
  const stage = process.env.NEXT_PUBLIC_STAGE;
  if (stage) {
    posthog.register({ environment: stage });
  }
  posthogInitialized = true;
}

if (typeof window !== "undefined") {
  initializePostHog();
}

export function PostHogProvider({ children }: { children: ReactNode }) {
  return <>{children}</>;
}
